import { useCallback, useEffect, useRef, useState } from 'react';
import { visualSeatClass } from '../utils/tableSeats';
import {
  ANIM_GAP_MS,
  FLY_WATCHDOG_MS,
  GATHER_WATCHDOG_MS,
  RECONCILE_ESCALATE_MS,
  trickKey,
  playId,
  trickOffset,
  seatReachOffset,
  findTrickWinnerFromCounts,
  emptyTrickCounts,
  countsEqual,
} from '../utils/trickAnimation';

function cloneCounts(counts) {
  return { ...emptyTrickCounts(), ...counts };
}

function registryIds(registry) {
  return new Set(registry.keys());
}

function isGathering(registry) {
  for (const sprite of registry.values()) {
    if (sprite.phase === 'gathering') return true;
  }
  return false;
}

function hasFlyingSprite(registry) {
  for (const sprite of registry.values()) {
    if (sprite.phase === 'flying') return true;
  }
  return false;
}

function epochSprites(registry, epoch) {
  return [...registry.values()].filter((s) => s.trickEpoch === epoch);
}

function isAnimPipelineActive(registry, refs) {
  return refs.animLockRef.current
    || hasFlyingSprite(registry)
    || isGathering(registry)
    || refs.collectingEpochRef.current != null
    || refs.flySpawnQueueRef.current.length > 0;
}

function restingTrickCount(registry) {
  let n = 0;
  for (const sprite of registry.values()) {
    if (sprite.phase === 'resting') n += 1;
  }
  return n;
}

/**
 * Client-side trick card registry with a strict animation pipeline:
 * one fly at a time, 30ms gaps between phases, gather only after all rests settle.
 */
export function useTrickCards(game, viewerSeat, layout) {
  const { trickDist, handReach } = layout || {};

  const [trickCards, setTrickCards] = useState([]);
  const [displayedTrickCounts, setDisplayedTrickCounts] = useState(emptyTrickCounts);

  const registryRef = useRef(new Map());
  const queueRef = useRef([]);
  const flySpawnQueueRef = useRef([]);
  const trickEpochRef = useRef(0);
  const collectingEpochRef = useRef(null);
  const gatherKeyRef = useRef(null);
  const displayedRef = useRef(emptyTrickCounts());
  const handNumberRef = useRef(null);
  const lastReconcileAtRef = useRef(0);
  const reconcileBurstRef = useRef(0);
  const flyTimersRef = useRef(new Map());
  const gatherWatchdogRef = useRef(null);
  const pipelineTimerRef = useRef(null);
  const animLockRef = useRef(false);
  const gameRef = useRef(game);
  const pumpRef = useRef(() => {});
  const lastTrickKeyRef = useRef('');
  const gatherCompleteFiredRef = useRef(false);
  gameRef.current = game;

  const pipelineRefs = useRef({
    animLockRef,
    collectingEpochRef,
    flySpawnQueueRef,
  });
  pipelineRefs.current = { animLockRef, collectingEpochRef, flySpawnQueueRef };

  const syncRender = useCallback(() => {
    const list = [...registryRef.current.values()].sort((a, b) => {
      if (a.trickEpoch !== b.trickEpoch) return a.trickEpoch - b.trickEpoch;
      return a.layerIndex - b.layerIndex;
    });
    setTrickCards(list);
  }, []);

  const clearFlyTimer = useCallback((id) => {
    const t = flyTimersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      flyTimersRef.current.delete(id);
    }
  }, []);

  const clearPipelineTimer = useCallback(() => {
    if (pipelineTimerRef.current) {
      clearTimeout(pipelineTimerRef.current);
      pipelineTimerRef.current = null;
    }
  }, []);

  const clearGatherWatchdog = useCallback(() => {
    if (gatherWatchdogRef.current) {
      clearTimeout(gatherWatchdogRef.current);
      gatherWatchdogRef.current = null;
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    for (const t of flyTimersRef.current.values()) clearTimeout(t);
    flyTimersRef.current.clear();
    clearPipelineTimer();
    clearGatherWatchdog();
    animLockRef.current = false;
  }, [clearPipelineTimer, clearGatherWatchdog]);

  const schedulePipeline = useCallback((delayMs, fn) => {
    if (delayMs <= 0) {
      fn();
      return;
    }
    clearPipelineTimer();
    pipelineTimerRef.current = setTimeout(() => {
      pipelineTimerRef.current = null;
      fn();
    }, delayMs);
  }, [clearPipelineTimer]);

  const isGatherBlocked = useCallback(() => {
    return collectingEpochRef.current != null || isGathering(registryRef.current);
  }, []);

  const knownPlayIds = useCallback(() => {
    const ids = registryIds(registryRef.current);
    for (const item of queueRef.current) {
      const id = playId(item);
      if (id) ids.add(id);
    }
    for (const item of flySpawnQueueRef.current) {
      const id = playId(item.play);
      if (id) ids.add(id);
    }
    return ids;
  }, []);

  const detectDesync = useCallback((serverTrick, serverCounts) => {
    const registry = registryRef.current;
    const refs = pipelineRefs.current;
    if (isAnimPipelineActive(registry, refs)) {
      return null;
    }

    const known = knownPlayIds();
    const gathering = isGathering(registry);
    const fullRestingTrick = restingTrickCount(registry) === 4;

    if (serverTrick.every((p) => known.has(playId(p)))) {
      return null;
    }

    const serverIds = new Set(serverTrick.map((p) => playId(p)).filter(Boolean));
    const clientVisible = [...registry.values()].filter(
      (s) => s.phase === 'flying' || s.phase === 'resting',
    );

    for (const sprite of clientVisible) {
      if (!serverIds.has(sprite.id) && !gathering) {
        return { mode: 'snap', reason: 'phantom' };
      }
    }

    if (!gathering && serverTrick.length === 0 && clientVisible.length > 0) {
      if (fullRestingTrick || collectingEpochRef.current != null) {
        return null;
      }
      return { mode: 'snap', reason: 'missed-resolve' };
    }

    if (!gathering && serverTrick.length < clientVisible.length) {
      return { mode: 'snap', reason: 'trick-shrink' };
    }

    if (!gathering && fullRestingTrick) {
      return null;
    }

    const winner = findTrickWinnerFromCounts(serverCounts, displayedRef.current);
    if (!gathering && winner && clientVisible.length === 0 && serverTrick.length > 0) {
      return { mode: 'snap', reason: 'counts-ahead' };
    }

    let maxJump = 0;
    for (const seat of ['north', 'east', 'south', 'west']) {
      const jump = (serverCounts?.[seat] || 0) - (displayedRef.current?.[seat] || 0);
      if (jump > maxJump) maxJump = jump;
    }
    if (!gathering && maxJump > 1) {
      return { mode: 'flush', reason: 'count-jump' };
    }

    return null;
  }, [knownPlayIds]);

  const snapFromServer = useCallback((serverTrick, serverCounts, epoch) => {
    clearAllTimers();
    registryRef.current.clear();
    queueRef.current = [];
    flySpawnQueueRef.current = [];
    collectingEpochRef.current = null;
    gatherKeyRef.current = null;
    lastTrickKeyRef.current = trickKey(serverTrick);
    gatherCompleteFiredRef.current = false;

    for (let i = 0; i < serverTrick.length; i++) {
      const play = serverTrick[i];
      const id = playId(play);
      if (!id) continue;
      const visual = visualSeatClass(play.seat, viewerSeat);
      // Rest at cardinal direction from the seat that played
      const off = trickOffset(visual, trickDist);
      registryRef.current.set(id, {
        id,
        seat: play.seat,
        card: { ...play.card },
        visualSeat: visual,
        phase: 'resting',
        layerIndex: i,
        trickEpoch: epoch,
        gatherDelay: 0,
        restX: off.x,
        restY: off.y,
        restRot: off.rot,
      });
    }

    displayedRef.current = cloneCounts(serverCounts);
    setDisplayedTrickCounts(cloneCounts(serverCounts));
    syncRender();
  }, [clearAllTimers, syncRender, trickDist, viewerSeat]);

  const flushFromServer = useCallback((serverCounts) => {
    clearAllTimers();
    registryRef.current.clear();
    queueRef.current = [];
    flySpawnQueueRef.current = [];
    collectingEpochRef.current = null;
    gatherKeyRef.current = null;
    lastTrickKeyRef.current = '';
    gatherCompleteFiredRef.current = false;
    displayedRef.current = cloneCounts(serverCounts);
    setDisplayedTrickCounts(cloneCounts(serverCounts));
    syncRender();
  }, [clearAllTimers, syncRender]);

  const maybeReconcile = useCallback((serverGame) => {
    const serverTrick = serverGame?.currentTrick || [];
    const serverCounts = serverGame?.trickWonCounts || emptyTrickCounts();
    const desync = detectDesync(serverTrick, serverCounts);
    if (!desync) return false;

    const now = Date.now();
    if (now - lastReconcileAtRef.current < RECONCILE_ESCALATE_MS) {
      reconcileBurstRef.current += 1;
    } else {
      reconcileBurstRef.current = 1;
    }
    lastReconcileAtRef.current = now;

    const mode = reconcileBurstRef.current >= 2 ? 'flush' : desync.mode;

    if (mode === 'flush') {
      flushFromServer(serverCounts);
    } else {
      snapFromServer(serverTrick, serverCounts, trickEpochRef.current);
    }
    return true;
  }, [detectDesync, flushFromServer, snapFromServer]);

  const settleFlying = useCallback((id, afterGap) => {
    const sprite = registryRef.current.get(id);
    if (!sprite || sprite.phase !== 'flying') return;
    const off = trickOffset(sprite.visualSeat, trickDist);
    sprite.phase = 'resting';
    sprite.restX = off.x;
    sprite.restY = off.y;
    sprite.restRot = off.rot;
    syncRender();
    schedulePipeline(afterGap ? ANIM_GAP_MS : 0, () => {
      animLockRef.current = false;
      pumpRef.current();
    });
  }, [schedulePipeline, syncRender, trickDist]);

  const spawnFlyingNow = useCallback((play, epoch, layerIndex) => {
    const id = playId(play);
    if (!id) {
      animLockRef.current = false;
      pumpRef.current();
      return;
    }

    const existing = registryRef.current.get(id);
    if (existing) {
      if (existing.phase === 'flying' || existing.phase === 'resting' || existing.phase === 'gathering') {
        animLockRef.current = false;
        pumpRef.current();
        return;
      }
    }

    animLockRef.current = true;
    const visual = visualSeatClass(play.seat, viewerSeat);
    registryRef.current.set(id, {
      id,
      seat: play.seat,
      card: { ...play.card },
      visualSeat: visual,
      phase: 'flying',
      layerIndex,
      trickEpoch: epoch,
      gatherDelay: 0,
      flySeq: (existing?.flySeq ?? 0) + 1,
    });
    syncRender();

    clearFlyTimer(id);
    flyTimersRef.current.set(id, setTimeout(() => {
      flyTimersRef.current.delete(id);
      settleFlying(id, true);
    }, FLY_WATCHDOG_MS));
  }, [clearFlyTimer, settleFlying, syncRender, viewerSeat]);

  const enqueueFly = useCallback((play, epoch, layerIndex) => {
    const id = playId(play);
    if (!id) return;
    if (registryRef.current.has(id)) return;
    if (flySpawnQueueRef.current.some((item) => playId(item.play) === id)) return;
    flySpawnQueueRef.current.push({ play, epoch, layerIndex });
    schedulePipeline(0, () => pumpRef.current());
  }, [schedulePipeline]);

  const finishGather = useCallback((serverCounts) => {
    if (collectingEpochRef.current == null && !isGathering(registryRef.current)) return;
    clearGatherWatchdog();
    for (const [id, sprite] of registryRef.current) {
      if (sprite.phase === 'gathering') registryRef.current.delete(id);
    }
    collectingEpochRef.current = null;
    gatherKeyRef.current = null;
    trickEpochRef.current += 1;
    displayedRef.current = cloneCounts(serverCounts);
    setDisplayedTrickCounts(cloneCounts(serverCounts));
    syncRender();
    schedulePipeline(ANIM_GAP_MS, () => {
      animLockRef.current = false;
      const serverTrick = gameRef.current?.currentTrick || [];
      drainQueueRef.current(serverTrick);
      pumpRef.current();
    });
  }, [clearGatherWatchdog, schedulePipeline, syncRender]);

  const drainQueueRef = useRef(() => {});

  const startGather = useCallback((serverTrick, serverCounts) => {
    const epoch = trickEpochRef.current;
    const key = trickKey(serverTrick);
    if (gatherKeyRef.current === key || collectingEpochRef.current != null) return;
    if (animLockRef.current || hasFlyingSprite(registryRef.current)) return;
    if (flySpawnQueueRef.current.length > 0) return;

    const sprites = epochSprites(registryRef.current, epoch);
    if (sprites.length !== 4 || !sprites.every((s) => s.phase === 'resting')) return;

    const winnerSeat = findTrickWinnerFromCounts(serverCounts, displayedRef.current);
    if (!winnerSeat) return;

    const winnerVisual = visualSeatClass(winnerSeat, viewerSeat);
    const winnerReach = seatReachOffset(winnerVisual, Math.round(handReach * 0.82));
    const winnerRot = winnerReach.rot ?? 0;

    gatherKeyRef.current = key;
    collectingEpochRef.current = epoch;
    animLockRef.current = true;
    gatherCompleteFiredRef.current = false;

    sprites.sort((a, b) => a.layerIndex - b.layerIndex).forEach((sprite, layerIdx) => {
      // Mid keyframe: pile tightly at table center, then fly to winner
      const stackX = (layerIdx - 1.5) * 4;
      const stackY = (layerIdx - 1.5) * 3;
      const endX = winnerReach.x + (layerIdx - 1.5) * 2;
      const endY = winnerReach.y + layerIdx * 1.5;
      Object.assign(sprite, {
        phase: 'gathering',
        gatherDelay: 0,
        gx0: sprite.restX,
        gy0: sprite.restY,
        gr0: sprite.restRot,
        gxs: stackX,
        gys: stackY,
        gr1: winnerRot,
        gx1: endX,
        gy1: endY,
        zIndex: layerIdx + 2,
      });
    });

    syncRender();
    clearGatherWatchdog();
    gatherWatchdogRef.current = setTimeout(() => {
      gatherWatchdogRef.current = null;
      finishGather(serverCounts);
    }, GATHER_WATCHDOG_MS);
  }, [
    clearGatherWatchdog,
    finishGather,
    handReach,
    syncRender,
    viewerSeat,
  ]);

  const pumpAnimation = useCallback(() => {
    if (animLockRef.current) return;
    if (collectingEpochRef.current != null || isGathering(registryRef.current)) return;
    if (hasFlyingSprite(registryRef.current)) return;

    if (flySpawnQueueRef.current.length > 0) {
      const { play, epoch, layerIndex } = flySpawnQueueRef.current.shift();
      spawnFlyingNow(play, epoch, layerIndex);
      return;
    }

    const current = gameRef.current;
    const serverTrick = current?.currentTrick || [];

    if (current?.phase === 'trick_resolve' && serverTrick.length === 4) {
      const sprites = epochSprites(registryRef.current, trickEpochRef.current);
      const allResting = sprites.length === 4 && sprites.every((s) => s.phase === 'resting');
      if (allResting) {
        schedulePipeline(ANIM_GAP_MS, () => {
          if (!animLockRef.current && !hasFlyingSprite(registryRef.current)
              && flySpawnQueueRef.current.length === 0) {
            startGather(serverTrick, current.trickWonCounts || emptyTrickCounts());
          } else {
            pumpRef.current();
          }
        });
        return;
      }
    }

    drainQueueRef.current(serverTrick);
  }, [schedulePipeline, spawnFlyingNow, startGather]);

  pumpRef.current = pumpAnimation;

  const drainQueue = useCallback((serverTrick) => {
    if (collectingEpochRef.current != null || isGathering(registryRef.current)) return;
    if (animLockRef.current || hasFlyingSprite(registryRef.current)) return;

    const serverIds = new Set(serverTrick.map((p) => playId(p)).filter(Boolean));
    const epoch = trickEpochRef.current;
    const deferred = [];

    while (queueRef.current.length > 0) {
      const item = queueRef.current[0];
      const id = playId(item);
      if (!id) {
        queueRef.current.shift();
        continue;
      }
      if (!serverIds.has(id)) {
        deferred.push(queueRef.current.shift());
        continue;
      }
      if (registryRef.current.has(id)) {
        queueRef.current.shift();
        continue;
      }
      const layerIndex = item.serverTrickIndex ?? serverTrick.findIndex(
        (p) => playId(p) === id,
      );
      queueRef.current.shift();
      enqueueFly(item, epoch, layerIndex >= 0 ? layerIndex : 0);
    }

    queueRef.current.push(...deferred);
  }, [enqueueFly]);

  drainQueueRef.current = drainQueue;

  const ingestServerPlays = useCallback((serverTrick) => {
    const known = knownPlayIds();
    const epoch = trickEpochRef.current;
    const gatherBlocked = isGatherBlocked();

    for (let i = 0; i < serverTrick.length; i++) {
      const play = serverTrick[i];
      const id = playId(play);
      if (!id || known.has(id)) continue;

      const item = { seat: play.seat, card: { ...play.card }, serverTrickIndex: i };
      if (gatherBlocked) {
        queueRef.current.push(item);
      } else {
        enqueueFly(item, epoch, i);
      }
      known.add(id);
    }
  }, [enqueueFly, isGatherBlocked, knownPlayIds]);

  const onFlyComplete = useCallback((id) => {
    clearFlyTimer(id);
    settleFlying(id, true);
  }, [clearFlyTimer, settleFlying]);

  const onGatherComplete = useCallback(() => {
    if (collectingEpochRef.current == null || gatherCompleteFiredRef.current) return;
    gatherCompleteFiredRef.current = true;
    const current = gameRef.current;
    const serverCounts = current?.trickWonCounts || emptyTrickCounts();
    clearGatherWatchdog();
    schedulePipeline(ANIM_GAP_MS, () => {
      finishGather(serverCounts);
    });
  }, [clearGatherWatchdog, finishGather, schedulePipeline]);

  useEffect(() => () => clearAllTimers(), [clearAllTimers]);

  useEffect(() => {
    if (!game || trickDist == null || handReach == null) {
      clearAllTimers();
      registryRef.current.clear();
      queueRef.current = [];
      flySpawnQueueRef.current = [];
      trickEpochRef.current = 0;
      collectingEpochRef.current = null;
      gatherKeyRef.current = null;
      handNumberRef.current = null;
      lastTrickKeyRef.current = '';
      gatherCompleteFiredRef.current = false;
      displayedRef.current = emptyTrickCounts();
      setDisplayedTrickCounts(emptyTrickCounts());
      setTrickCards([]);
      return;
    }

    const serverTrick = game.currentTrick || [];
    const serverCounts = game.trickWonCounts || emptyTrickCounts();
    const phase = game.phase;
    const serverTrickKey = trickKey(serverTrick);
    const registry = registryRef.current;
    const pipelineActive = isAnimPipelineActive(registry, pipelineRefs.current);
    const trickKeyChanged = serverTrickKey !== lastTrickKeyRef.current;

    if (game.handNumber !== handNumberRef.current) {
      handNumberRef.current = game.handNumber;
      lastTrickKeyRef.current = serverTrickKey;
      trickEpochRef.current = 0;
      reconcileBurstRef.current = 0;
      snapFromServer(serverTrick, serverCounts, 0);
      ingestServerPlays(serverTrick);
      schedulePipeline(0, () => pumpRef.current());
      return;
    }

    if (trickKeyChanged) {
      lastTrickKeyRef.current = serverTrickKey;
      ingestServerPlays(serverTrick);
    }

    if (pipelineActive) {
      if (trickKeyChanged) {
        schedulePipeline(0, () => pumpRef.current());
      }
      return;
    }

    if (maybeReconcile(game)) {
      schedulePipeline(0, () => pumpRef.current());
      return;
    }

    if (phase !== 'trick_resolve' && !isGatherBlocked()) {
      gatherKeyRef.current = null;
    }

    if (
      collectingEpochRef.current == null
      && !isGathering(registry)
      && phase !== 'trick_resolve'
      && !countsEqual(serverCounts, displayedRef.current)
    ) {
      displayedRef.current = cloneCounts(serverCounts);
      setDisplayedTrickCounts(cloneCounts(serverCounts));
    }

    if (trickKeyChanged || phase === 'trick_resolve') {
      schedulePipeline(0, () => pumpRef.current());
    }
  }, [
    game,
    trickDist,
    handReach,
    clearAllTimers,
    maybeReconcile,
    snapFromServer,
    ingestServerPlays,
    schedulePipeline,
    isGatherBlocked,
  ]);

  useEffect(() => {
    if (!gameRef.current || trickDist == null) return;
    if (isAnimPipelineActive(registryRef.current, pipelineRefs.current)) return;

    const registry = registryRef.current;
    let changed = false;
    for (const sprite of registry.values()) {
      if (sprite.phase === 'resting') {
        const visual = visualSeatClass(sprite.seat, viewerSeat);
        if (sprite.visualSeat !== visual) {
          sprite.visualSeat = visual;
          const off = trickOffset(visual, trickDist);
          sprite.restX = off.x;
          sprite.restY = off.y;
          sprite.restRot = off.rot;
          changed = true;
        }
      }
    }
    if (changed) syncRender();
  }, [viewerSeat, trickDist, syncRender]);

  return {
    trickCards,
    displayedTrickCounts,
    onFlyComplete,
    onGatherComplete,
  };
}